import {
  FeatureEventExperience,
  FeaturePrograms,
} from '@mdlbeasts/nofomo.ui.web';
import { Box, Container, NoSsr, Stack, Typography } from '@mui/material';
import { useRouter } from 'next/router';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { hasWindow } from 'utils/browser.utils';
import { IS_NEW_LINEUP_UI_ENABLED } from 'utils/env.utils';
import { isStructuredTextPresent } from 'utils/structured-text.utils';
import { aspectRatio } from 'utils/styles.utils';

import { Routes } from 'constants/routes.constants';
import { useEventDetailView, useOriginClick } from 'services/datalayer/browse';
import {
  EventCardFragment,
  PageEventFragment,
  VenueFragment,
} from 'types/api/dato-operations.types';
import { EventLowestPriceFragment } from 'types/api/tickets.types';

import { AffiliateNotice } from 'components/@affiliate/AffiliateNotice';
import { useAffiliateWatcher } from 'components/@affiliate/useAffiliateWatcher';
import DatoImage from 'components/@common/DatoImage';
import Description from 'components/@common/Description';
import Link from 'components/@common/Link';
import DynamicShareButton from 'components/@events/DynamicShareButton';
import EventCard from 'components/@events/EventCard';
import EventDetailPrice from 'components/@events/EventDetailPrice';
import EventHeader from 'components/@events/EventHeader';
import LineupItemList from 'components/@events/LineupItemList';
import { isPastEvent } from 'components/@events/utils';
import VenueCard from 'components/@events/VenueCard';
import ScrollableList from 'components/@lists/ScrollableList';
import AppNavigation from 'components/@navigation/AppNavigation';
import PageBreadcrumbs from 'components/@navigation/PageBreadcrumbs';

import SettingLinksList from './SettingLinksList';

interface Props {
  event: PageEventFragment;
  moreEvents?: Array<EventCardFragment>;
  city: string;
  venue: VenueFragment;
  onSocialShared?: (platform: string) => void;
  eventLowestPrice?: EventLowestPriceFragment | null;
  ticketingError: boolean;
}

const EventDetailContainer = ({
  event,
  moreEvents,
  onSocialShared,
  eventLowestPrice,
  ticketingError,
}: Props) => {
  const router = useRouter();
  useEventDetailView(event);

  /**
   * Affiliate watcher - syncs affiliate
   */
  useAffiliateWatcher();

  const handleClick = useOriginClick('Related', 'Event detail');
  const url = hasWindow() ? window.location.href : '';

  const handleProgramClick = (programPath: string, programName: string) => {
    router.push({
      pathname: `/events/${event.slug}/program`,
      query: { path: programPath, name: programName },
    });
  };

  const isDisabled = event.ticketsIntegration === 'disabled';
  const isTicketExternal =
    event.ticketsIntegration === 'external' && !!event.ticketsExternalUrl;
  const pastEvent = isPastEvent({
    endDatetime: event.endDatetime,
    startDatetime: event.startDatetime,
    timezone: event.timezone,
  });

  const showPrograms = IS_NEW_LINEUP_UI_ENABLED && !!event.programs?.length;

  // only show lineup when there are no programs - mainly XP
  const showLineup = showPrograms
    ? false
    : !IS_NEW_LINEUP_UI_ENABLED
      ? !!event.performers.length
      : !!event.performers.length || !!event.mdlbeastLineupPath;

  return (
    <>
      <AppNavigation hideBottomNavigation={true}>
        <PageBreadcrumbs
          items={[{ href: Routes.CollectionDetail, label: `${event.title}` }]}
        />
        <Container sx={{ px: { xs: 1, md: 2 } }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '6fr 1fr 5fr' },

              gridTemplateAreas: {
                xs: `'cover' 'description'`,
                md: `'description . cover'`,
              },

              alignItems: 'start',
              mb: { md: 15 },
              mt: { md: 3 },
            }}
          >
            <Stack
              sx={{
                gridArea: 'cover',
                position: { md: 'sticky' },
                top: 40,
              }}
            >
              <Box
                my={{ xs: '24px' }}
                sx={{ display: { xs: 'block', md: 'none' } }}
              >
                <AffiliateNotice eventName={event.title} />
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 6,
                  ...aspectRatio(1, 1),
                }}
              >
                <DatoImage
                  data={event.cover}
                  fill
                  priority
                  style={{ objectFit: 'cover' }}
                  sizes="512px"
                />
                <NoSsr>
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 5,
                      bottom: 5,
                    }}
                  >
                    <DynamicShareButton
                      url={url}
                      ariaTitle={event.title}
                      onShare={() => onSocialShared?.('url')}
                    />
                  </Box>
                </NoSsr>
              </Box>

              <Box
                sx={{
                  display: { xs: 'none', md: 'block' },
                  position: 'sticky',
                  bottom: 0,
                  border: '1px solid',
                  borderColor: 'grey.800',

                  bgcolor:
                    pastEvent ||
                    isDisabled ||
                    isTicketExternal ||
                    ticketingError
                      ? 'background.default'
                      : 'grey.900',

                  zIndex: 1000,
                  borderRadius: 4,
                  mt: 5,
                }}
              >
                <EventDetailPrice
                  eventLowestPrice={eventLowestPrice}
                  event={event}
                  isDisabled={isDisabled}
                  isTicketExternal={isTicketExternal}
                  isPastEvent={pastEvent}
                />
              </Box>
            </Stack>

            <Stack
              sx={{
                mx: { xs: 1, md: 0 },
                mt: { xs: 2, md: 0 },
                gridArea: 'description',
              }}
            >
              <Stack
                sx={{
                  mb: 8,
                }}
              >
                <Box
                  mb={{ md: '24px' }}
                  sx={{ display: { xs: 'none', md: 'block' } }}
                >
                  <AffiliateNotice eventName={event.title} />
                </Box>
                <Box
                  sx={{
                    mb: isStructuredTextPresent(event.description) ? 3 : 0,
                  }}
                >
                  <EventHeader event={event} />
                </Box>

                {isStructuredTextPresent(event.description) && (
                  <Box
                    sx={{
                      mb: 3,
                    }}
                  >
                    <Description description={event.description} />
                  </Box>
                )}

                <Box>
                  <FeatureEventExperience.EventExperience
                    linkedPages={event.linkedPages}
                    DatoImage={DatoImage}
                    Link={Link}
                  />
                </Box>
              </Stack>

              {showPrograms && (
                <FeaturePrograms.ProgramSection
                  programs={event.programs}
                  DatoImage={DatoImage}
                  onPress={(program) => {
                    if (program.xpProgramPath) {
                      handleProgramClick(program.xpProgramPath, program.name);
                    }
                  }}
                />
              )}

              {showLineup && (
                <Stack
                  sx={{
                    mb: { xs: 5, md: 8 },
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      mb: 2,
                      color: 'text.secondary',
                    }}
                  >
                    <FormattedMessage id="event.header.tab.lineup" />
                  </Typography>
                  <LineupItemList
                    performers={event.performers}
                    slug={event.slug}
                    mdlbeastLineupPath={event.mdlbeastLineupPath}
                    performersCount={event.performersCount}
                  />
                </Stack>
              )}

              <Stack>
                <Typography
                  variant="h6"
                  component="h2"
                  sx={{
                    mb: 2,
                    color: 'text.secondary',
                  }}
                >
                  <FormattedMessage id="event.header.tab.venue" />
                </Typography>
                <VenueCard venue={event.venue} />
              </Stack>

              <Box
                sx={{
                  my: { xs: 8, md: 0 },
                  mt: { md: 8 },
                }}
              >
                <SettingLinksList />
              </Box>
            </Stack>
          </Box>
        </Container>

        {!!moreEvents?.length && (
          <Stack
            sx={{
              mb: { xs: 8, md: 15 },
            }}
          >
            <Container>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                }}
              >
                <FormattedMessage id="event.header.tab.otherevents" />
              </Typography>
            </Container>

            <ScrollableList
              minWidthItem={{ xs: 260, md: 272 }}
              imageRefHeight={272}
              gap={3}
            >
              {moreEvents?.map((moreEvent) => (
                <EventCard
                  borderless={true}
                  event={moreEvent}
                  key={moreEvent.id}
                  onClick={handleClick}
                />
              ))}
            </ScrollableList>
          </Stack>
        )}
      </AppNavigation>
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.default',
          zIndex: 1000,
          borderTop: '1px solid',
          borderColor: 'grey.800',
        }}
      >
        <EventDetailPrice
          eventLowestPrice={eventLowestPrice}
          event={event}
          isDisabled={isDisabled}
          isTicketExternal={isTicketExternal}
          isPastEvent={pastEvent}
        />
      </Box>
    </>
  );
};

export default EventDetailContainer;
